import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save, Wrench } from 'lucide-react';
import { Button } from '@/components/Button';
import { Card, CardBody, CardHeader } from '@/components/Card';
import { EmptyState } from '@/components/EmptyState';
import { Skeleton, SkeletonRegion } from '@/components/Skeleton';
import { formatarDataHora, formatarDistancia } from '@/lib/format';
import { LinkDeChamado } from '@/features/tickets/navigation';
import { NomeDePessoa, useDicionarioDePessoas } from '@/features/tickets/pessoas';
import { listarChamados } from '@/features/tickets/tickets.api';
import { assinatura, useRascunho } from '@/lib/rascunho';
import { AvisoDeFalha, AvisoDeRegistroMudado } from '@/features/workflow/componentes';
import { classificarFalha, type Falha } from '@/features/workflow/recusa';
import { Campo, CLASSE_DE_CAMPO, Dado, Secao } from '@/features/changes/componentes';
import { EtiquetaDeSituacaoDeErro, Recorrencia } from '@/features/kedb/componentes';
import {
  alterarErroConhecido,
  chavesDeKedb,
  excluirErroConhecido,
  obterErroConhecido,
} from '@/features/kedb/kedb.api';
import { LinkDaBaseDeErros } from '@/features/kedb/navegacao';
import {
  RESUMO_DE_SITUACAO_DE_ERRO,
  ROTULO_DE_SITUACAO_DE_ERRO,
  SITUACOES_DE_ERRO,
  type ErroConhecido,
  type SituacaoDeErro,
} from '@/features/kedb/tipos';

/**
 * A ficha do erro conhecido.
 *
 * O que ela põe em primeiro plano é a **solução de contorno**: é a razão de o
 * registro existir, é o que o operador copia às duas da manhã, e enterrá-la
 * abaixo de causa raiz e metadados faria a tela servir a quem escreve em vez de
 * a quem lê.
 */
export function ErroConhecidoPage({ erroId }: { erroId: string }) {
  const consulta = useQuery({
    queryKey: chavesDeKedb.item(erroId),
    queryFn: () => obterErroConhecido(erroId),
  });

  if (consulta.isPending) {
    return (
      <SkeletonRegion label="Carregando o erro conhecido" className="mx-auto max-w-4xl space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-48 w-full" />
      </SkeletonRegion>
    );
  }

  if (consulta.isError) {
    return (
      <div className="mx-auto max-w-4xl space-y-3">
        <AvisoDeFalha
          falha={classificarFalha(consulta.error)}
          aoRecarregar={() => void consulta.refetch()}
        />
        <LinkDaBaseDeErros className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Erros conhecidos
        </LinkDaBaseDeErros>
      </div>
    );
  }

  const erro = consulta.data;

  return (
    // A chave remonta a ficha quando o erro conhecido muda: o formulário de
    // edição guarda o texto em estado local, e navegar de um erro para outro
    // sem remontar mostraria — e gravaria — o contorno do anterior.
    <div key={erro.id} className="mx-auto max-w-4xl space-y-6">
      <Cabecalho erro={erro} />
      <Contorno erro={erro} />
      <Edicao erro={erro} />
      <Incidentes erro={erro} />
    </div>
  );
}

function Cabecalho({ erro }: { erro: ErroConhecido }) {
  return (
    <header className="space-y-4">
      <LinkDaBaseDeErros className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground">
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Erros conhecidos
      </LinkDaBaseDeErros>

      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-sm font-semibold text-gradient">{erro.code}</span>
          <EtiquetaDeSituacaoDeErro situacao={erro.status} />
          <Recorrencia contagem={erro.occurrenceCount} />
        </div>
        <h1 className="text-2xl font-semibold leading-tight tracking-tight">{erro.title}</h1>
      </div>

      <dl className="grid gap-x-6 gap-y-3 rounded-lg border border-border bg-card px-5 py-4 text-sm shadow-card sm:grid-cols-3">
        <Dado
          termo="Problema de origem"
          valor={
            <LinkDeChamado
              ticketId={erro.problemTicket.id}
              className="font-mono text-xs font-semibold text-primary hover:underline"
              title={erro.problemTicket.subject}
            >
              {erro.problemTicket.number}
            </LinkDeChamado>
          }
        />
        <Dado
          termo="Correção definitiva"
          valor={
            erro.permanentFixTicket ? (
              <LinkDeChamado
                ticketId={erro.permanentFixTicket.id}
                className="font-mono text-xs font-semibold text-primary hover:underline"
                title={erro.permanentFixTicket.subject}
              >
                {erro.permanentFixTicket.number}
              </LinkDeChamado>
            ) : (
              <span
                className="italic text-muted-foreground"
                title="A mudança que elimina o erro de vez. Enquanto ela não subir, o contorno é o que existe."
              >
                ainda não há
              </span>
            )
          }
        />
        <Dado termo="Publicado em" valor={formatarDataHora(erro.createdAt)} />
      </dl>
    </header>
  );
}

/**
 * O contorno em destaque, e os sintomas ao lado.
 *
 * Os sintomas são o que faz a busca casar; o contorno é o que resolve a noite
 * de quem está atendendo. Os dois juntos, nesta ordem de peso.
 */
function Contorno({ erro }: { erro: ErroConhecido }) {
  return (
    <Card>
      <CardHeader
        title="Solução de contorno"
        description="O que fazer agora, enquanto a correção definitiva não sobe."
      />
      <CardBody className="space-y-4">
        <div className="rounded-md border border-success/25 bg-success/5 px-4 py-3">
          <p className="whitespace-pre-wrap text-sm leading-relaxed">{erro.workaround}</p>
        </div>

        <Secao titulo="Sintomas">
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">
            {erro.symptoms}
          </p>
        </Secao>

        {erro.rootCause ? (
          <Secao titulo="Causa raiz">
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">
              {erro.rootCause}
            </p>
          </Secao>
        ) : null}
      </CardBody>
    </Card>
  );
}

// ── edição ─────────────────────────────────────────────────────────────────

function Edicao({ erro }: { erro: ErroConhecido }) {
  const cliente = useQueryClient();
  const [falha, setFalha] = useState<Falha | null>(null);

  /**
   * O formulário inteiro num rascunho marcado pelo conteúdo — ver
   * `@/lib/rascunho`.
   *
   * Semeado uma vez, ele ficava com o texto antigo depois de qualquer recarga
   * da consulta, e gravava por cima: o contorno reescrito por outra pessoa
   * seria apagado sem 409 nenhum. Num registro cujo valor é justamente o
   * contorno estar certo, esse é o pior defeito possível.
   */
  const rascunho = useRascunho(
    () => ({
      titulo: erro.title,
      sintomas: erro.symptoms,
      contorno: erro.workaround,
      causa: erro.rootCause ?? '',
      status: erro.status,
      correcao: erro.permanentFixTicketId ?? '',
    }),
    assinatura(
      erro.title,
      erro.symptoms,
      erro.workaround,
      erro.rootCause,
      erro.status,
      erro.permanentFixTicketId,
    ),
  );
  const { titulo, sintomas, contorno, causa, status, correcao } = rascunho.valor;
  const alterar = (parcial: Partial<typeof rascunho.valor>) =>
    rascunho.definir((atual) => ({ ...atual, ...parcial }));

  const mudancas = useQuery({
    queryKey: ['chamados', 'lista', 'mudancas-para-kedb'],
    queryFn: () => listarChamados({ type: ['CHANGE'], limit: 100 }),
  });

  const salvar = useMutation({
    mutationFn: () =>
      alterarErroConhecido(erro.id, {
        title: titulo.trim(),
        symptoms: sintomas.trim(),
        workaround: contorno.trim(),
        rootCause: causa.trim(),
        status,
        // `null` quando o combo está em "ainda não há": é assim que se
        // desvincula. Omitir a chave significaria "não mexa nisto", e o vínculo
        // ficaria — que é o "sucesso" sem efeito que esta tela dava antes.
        permanentFixTicketId: correcao === '' ? null : correcao,
        version: erro.version,
      }),
    onMutate: () => setFalha(null),
    onSuccess: () => {
      rascunho.confirmar();
      void cliente.invalidateQueries({ queryKey: chavesDeKedb.raiz });
    },
    onError: (erroDaChamada) => setFalha(classificarFalha(erroDaChamada)),
  });

  const excluir = useMutation({
    mutationFn: () => excluirErroConhecido(erro.id),
    onMutate: () => setFalha(null),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chavesDeKedb.raiz });
      window.history.back();
    },
    onError: (erroDaChamada) => setFalha(classificarFalha(erroDaChamada)),
  });

  const incompleto =
    rascunho.desatualizado ||
    titulo.trim().length < 3 ||
    sintomas.trim().length < 10 ||
    contorno.trim().length < 10;

  return (
    <Card>
      <CardHeader
        title="Editar"
        description="Manter o contorno vivo é o trabalho da KEDB: um contorno que deixou de funcionar é pior do que nenhum, porque alguém vai segui-lo."
        action={
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="secondary"
              loading={salvar.isPending}
              disabled={incompleto}
              onClick={() => salvar.mutate()}
              icon={<Save className="h-4 w-4" aria-hidden />}
            >
              Salvar
            </Button>
            <Button
              size="sm"
              variant="danger"
              loading={excluir.isPending}
              onClick={() => excluir.mutate()}
            >
              Excluir
            </Button>
          </div>
        }
      />
      <CardBody className="space-y-4">
        <Campo rotulo="Título" htmlFor="editar-titulo">
          <input
            id="editar-titulo"
            value={titulo}
            onChange={(evento) => alterar({ titulo: evento.target.value })}
            className={CLASSE_DE_CAMPO}
          />
        </Campo>

        <Campo rotulo="Sintomas" htmlFor="editar-sintomas">
          <textarea
            id="editar-sintomas"
            rows={4}
            value={sintomas}
            onChange={(evento) => alterar({ sintomas: evento.target.value })}
            className={CLASSE_DE_CAMPO}
          />
        </Campo>

        <Campo rotulo="Solução de contorno" htmlFor="editar-contorno">
          <textarea
            id="editar-contorno"
            rows={4}
            value={contorno}
            onChange={(evento) => alterar({ contorno: evento.target.value })}
            className={CLASSE_DE_CAMPO}
          />
        </Campo>

        <Campo rotulo="Causa raiz" htmlFor="editar-causa">
          <textarea
            id="editar-causa"
            rows={3}
            value={causa}
            onChange={(evento) => alterar({ causa: evento.target.value })}
            className={CLASSE_DE_CAMPO}
          />
        </Campo>

        <Campo
          rotulo="Situação"
          htmlFor="editar-status"
          dica={RESUMO_DE_SITUACAO_DE_ERRO[status]}
        >
          <select
            id="editar-status"
            value={status}
            onChange={(evento) => alterar({ status: evento.target.value as SituacaoDeErro })}
            className={CLASSE_DE_CAMPO}
          >
            {SITUACOES_DE_ERRO.map((valor) => (
              <option key={valor} value={valor}>
                {ROTULO_DE_SITUACAO_DE_ERRO[valor]}
              </option>
            ))}
          </select>
        </Campo>

        <Campo
          rotulo="Correção definitiva"
          htmlFor="editar-correcao"
          dica="Precisa ser uma mudança: é ela que elimina o erro, e o contorno existe só até ela subir. O servidor recusa qualquer outro tipo de chamado. Voltar para “ainda não há” desvincula de verdade — a correção deixa de existir para este erro, e o contorno volta a ser o que há."
        >
          <select
            id="editar-correcao"
            value={correcao}
            onChange={(evento) => alterar({ correcao: evento.target.value })}
            className={CLASSE_DE_CAMPO}
          >
            <option value="">Ainda não há</option>
            {(mudancas.data?.items ?? []).map((chamado) => (
              <option key={chamado.id} value={chamado.id}>
                {chamado.number} — {chamado.subject}
              </option>
            ))}
          </select>
        </Campo>

        {rascunho.desatualizado ? <AvisoDeRegistroMudado aoDescartar={rascunho.redefinir} /> : null}

        {falha ? (
          <AvisoDeFalha
            falha={falha}
            aoRecarregar={() => {
              rascunho.redefinir();
              void cliente.refetchQueries({ queryKey: chavesDeKedb.item(erro.id) });
            }}
          />
        ) : null}
      </CardBody>
    </Card>
  );
}

// ── incidentes ─────────────────────────────────────────────────────────────

/**
 * Quem já topou com este erro.
 *
 * A lista traz também as sugestões **recusadas** (`applied: false`), e elas são
 * o dado mais útil da tela: sugestão recusada é o sinal de que a base está
 * devolvendo ruído, e sem registro ninguém descobre.
 */
function Incidentes({ erro }: { erro: ErroConhecido }) {
  const pessoas = useDicionarioDePessoas(erro.incidents.map((linha) => linha.appliedById));
  const aplicados = erro.incidents.filter((linha) => linha.applied);
  const recusados = erro.incidents.filter((linha) => !linha.applied);

  return (
    <Card>
      <CardHeader
        title="Incidentes"
        description={`${aplicados.length} com o contorno aplicado, ${recusados.length} em que a sugestão foi recusada. Só o primeiro grupo conta para o contador — e é ele que justifica priorizar a correção definitiva.`}
      />
      <CardBody>
        {erro.incidents.length === 0 ? (
          <EmptyState
            icon={<Wrench className="h-6 w-6" aria-hidden />}
            title="Nenhum incidente vinculado ainda"
            description="O vínculo nasce no atendimento: quando o operador aplica o contorno a partir da sugestão dentro do chamado."
          />
        ) : (
          <ul className="divide-y divide-border rounded-md border border-border">
            {erro.incidents.map((linha) => (
              <li key={linha.ticketId} className="space-y-1 px-3 py-2 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <LinkDeChamado
                    ticketId={linha.ticketId}
                    className="font-mono text-xs font-semibold text-primary hover:underline"
                  >
                    {linha.ticket.number}
                  </LinkDeChamado>
                  <span className="min-w-0 flex-1 truncate">{linha.ticket.subject}</span>
                  <span
                    className={
                      linha.applied
                        ? 'text-xs font-semibold text-success'
                        : 'text-xs font-semibold text-muted-foreground'
                    }
                  >
                    {linha.applied ? 'contorno aplicado' : 'sugestão recusada'}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  <NomeDePessoa id={linha.appliedById} pessoas={pessoas} semId="sistema" />{' '}
                  <span title={formatarDataHora(linha.appliedAt)}>
                    {formatarDistancia(linha.appliedAt)}
                  </span>
                  {linha.note ? ` — “${linha.note}”` : ''}
                </p>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}
